import { LINK_STATUSES, RAW_CONDITIONS } from "@pokifolio/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ExternalLink, Search, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { useApi } from "../../app/api-context";
import { Pagination } from "../../components/data-table/pagination";
import { PageHeader } from "../../components/page-header";
import { ErrorAlert } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input, Select } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useDebouncedValue } from "../../lib/use-debounced-value";
import { useCurrentUser } from "../auth/session";
import { RAW_CONDITION_LABELS, formatDate } from "../research/format";
import {
  LINK_STATUS_LABELS,
  LINK_STATUS_TONES,
  describeLinkCondition,
  describeLinkPrice,
  describeSource,
} from "./card-format";
import { useListings } from "./card-queries";
import { DealControls, PriorityBadge } from "./deal-controls";
import type { ListingSearch, ListingSort, ListingView } from "./listing-filters";

interface ListingsPageProps {
  readonly filters: ListingSearch;
  readonly onFiltersChange: (next: ListingSearch) => void;
}

const VIEWS: readonly (readonly [ListingView, string])[] = [
  ["active", "Active"],
  ["negotiating", "In negotiation"],
  ["purchased", "Purchased"],
  ["closed", "Closed"],
  ["all", "All"],
];

const SORTS: readonly (readonly [ListingSort, string])[] = [
  ["latest", "Latest added"],
  ["price_asc", "Price: low to high"],
  ["price_desc", "Price: high to low"],
  ["priority", "Priority"],
];

/** Every listing across all cards in one table: the admin's working lists, filters and sorting (D-66, D-67). */
export function ListingsPage({ filters, onFiltersChange }: ListingsPageProps) {
  const [text, setText] = useState(filters.q ?? "");
  const [collection, setCollection] = useState(filters.setName ?? "");
  const q = useDebouncedValue(text.trim(), 300);
  const setName = useDebouncedValue(collection.trim(), 300);
  const view = filters.view ?? "active";
  const list = useListings({ ...filters, view, q, setName });
  const me = useCurrentUser();
  const api = useApi();
  const platforms = useQuery({ queryKey: ["cards", "platforms"], queryFn: () => api.cardPlatforms() });
  const ids = {
    find: useId(),
    collection: useId(),
    status: useId(),
    condition: useId(),
    platform: useId(),
    state: useId(),
    scope: useId(),
    sort: useId(),
  };

  const typed = useRef({ q, setName });
  useEffect(() => {
    if (typed.current.q === q && typed.current.setName === setName) return;
    typed.current = { q, setName };
    onFiltersChange({
      ...filters,
      q: q === "" ? undefined : q,
      setName: setName === "" ? undefined : setName,
      page: undefined,
    });
  }, [q, setName, filters, onFiltersChange]);

  const set = (patch: Partial<ListingSearch>) => onFiltersChange({ ...filters, ...patch, page: undefined });
  const filtered =
    q !== "" ||
    setName !== "" ||
    filters.status !== undefined ||
    filters.condition !== undefined ||
    filters.itemState !== undefined ||
    filters.platform !== undefined;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Listings" description="Every listing on every card. Pick a list, filter and sort." />

      <div role="tablist" aria-label="Lists" className="flex flex-wrap gap-1 border-b">
        {VIEWS.map(([v, label]) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            onClick={() => set({ view: v === "active" ? undefined : v })}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${view === v ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-48 flex-1 flex-col gap-1 sm:max-w-xs">
          <label htmlFor={ids.find} className="text-xs font-medium text-muted-foreground">
            Find
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id={ids.find}
              type="search"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Card, website or link"
              className="ps-8"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={ids.collection} className="text-xs font-medium text-muted-foreground">
            Collection
          </label>
          <Input
            id={ids.collection}
            value={collection}
            onChange={(e) => setCollection(e.target.value)}
            placeholder="e.g. Roaring Skies"
            className="w-44"
          />
        </div>
        <FilterSelect
          id={ids.platform}
          label="Platform"
          value={filters.platform}
          onChange={(v) => set({ platform: v })}
          options={(platforms.data ?? []).map((p) => [p, p])}
          all="All platforms"
        />
        <FilterSelect
          id={ids.condition}
          label="Condition"
          value={filters.condition}
          onChange={(v) => set({ condition: v as ListingSearch["condition"] })}
          options={RAW_CONDITIONS.map((c) => [c, RAW_CONDITION_LABELS[c]])}
          all="All conditions"
        />
        <FilterSelect
          id={ids.status}
          label="Approval"
          value={filters.status}
          onChange={(v) => set({ status: v as ListingSearch["status"] })}
          options={LINK_STATUSES.map((s) => [s, LINK_STATUS_LABELS[s]])}
          all="Any"
        />
        <FilterSelect
          id={ids.state}
          label="New or used"
          value={filters.itemState}
          onChange={(v) => set({ itemState: v as ListingSearch["itemState"] })}
          options={[
            ["new", "New"],
            ["used", "Used"],
          ]}
          all="New and used"
        />
        <FilterSelect
          id={ids.scope}
          label="Added by"
          value={filters.scope}
          onChange={(v) => set({ scope: v === "mine" ? "mine" : undefined })}
          options={[["mine", "Me"]]}
          all="Everyone"
        />
        <div className="flex flex-col gap-1">
          <label htmlFor={ids.sort} className="text-xs font-medium text-muted-foreground">
            Sort
          </label>
          <Select
            id={ids.sort}
            value={filters.sort ?? "latest"}
            onChange={(e) =>
              set({
                sort: e.target.value === "latest" ? undefined : (e.target.value as ListingSearch["sort"]),
              })
            }
            className="w-44"
          >
            {SORTS.map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        {filtered && (
          <Button
            variant="ghost"
            onClick={() => {
              setText("");
              setCollection("");
              onFiltersChange({
                ...(filters.view === undefined ? {} : { view: filters.view }),
                ...(filters.sort === undefined ? {} : { sort: filters.sort }),
                ...(filters.scope === undefined ? {} : { scope: filters.scope }),
              });
            }}
          >
            Reset
            <X aria-hidden="true" />
          </Button>
        )}
      </div>

      {list.isPending ? (
        <Card className="flex flex-col gap-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </Card>
      ) : list.isError ? (
        <ErrorAlert error={list.error} />
      ) : list.data.total === 0 ? (
        <Card className="py-12 text-center">
          <p className="text-sm text-muted-foreground">
            {filtered ? "No listings match these filters." : "No listings in this list."}
          </p>
        </Card>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
            <Table className="min-w-[1100px]">
              <caption className="sr-only">Listings</caption>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {["Card", "Where", "Price", "Condition", "Approval", "Status", "Added by", "Updated"].map(
                    (h) => (
                      <TableHead
                        key={h}
                        scope="col"
                        className="text-xs uppercase tracking-wide text-muted-foreground"
                      >
                        {h}
                      </TableHead>
                    ),
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.data.map(({ cardName, link }) => (
                  <TableRow key={link.id} className="align-top">
                    <TableCell>
                      <Link
                        to="/cards/$cardId"
                        params={{ cardId: link.cardId }}
                        className="font-medium hover:underline"
                      >
                        {cardName}
                      </Link>
                    </TableCell>
                    <TableCell className="whitespace-normal">
                      {link.url === null ? (
                        <span className="font-medium">{link.website}</span>
                      ) : (
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 hover:underline"
                        >
                          {link.website}
                          <ExternalLink className="size-3.5" aria-hidden="true" />
                          <span className="sr-only">(opens in a new tab)</span>
                        </a>
                      )}
                      <p className="text-xs text-muted-foreground">{describeSource(link)}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5 font-medium">
                        {describeLinkPrice(link)}
                        <PriorityBadge priority={link.priority} />
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-normal text-muted-foreground">
                      {describeLinkCondition(link)}
                    </TableCell>
                    <TableCell>
                      <Badge tone={LINK_STATUS_TONES[link.status]}>{LINK_STATUS_LABELS[link.status]}</Badge>
                    </TableCell>
                    <TableCell>
                      <DealControls link={link} me={me} />
                    </TableCell>
                    <TableCell>{link.addedBy.id === me.user.id ? "You" : link.addedBy.name}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(link.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={list.data.page}
            pageSize={list.data.pageSize}
            total={list.data.total}
            busy={list.isPlaceholderData}
            onPage={(page) => onFiltersChange({ ...filters, page: page > 1 ? page : undefined })}
            noun="listings"
          />
        </>
      )}
    </div>
  );
}

function FilterSelect({
  id,
  label,
  value,
  onChange,
  options,
  all,
}: {
  id: string;
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: readonly (readonly [string, string])[];
  all: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <Select
        id={id}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? undefined : e.target.value)}
        className="w-40"
      >
        <option value="">{all}</option>
        {options.map(([v, label]) => (
          <option key={v} value={v}>
            {label}
          </option>
        ))}
      </Select>
    </div>
  );
}
